import { useTraduccion } from '@tallerya/i18n';
import type { MouseEvent, ReactNode } from 'react';
import { Ayuda } from '../Ayuda';
import { Boton } from '../Boton';
import { Menu, type ElementoMenu } from '../Menu';
import { useEsCelular } from '../useEsCelular';
import { Paginacion } from './Paginacion';
import './listado.css';

// El patrón de listado (F-7). Sale de «Productos/Servicios - Listado» del Figma
// y lo repiten las seis maestras: una maestra declara sus columnas, sus filtros
// y sus textos, y no arma ni una tabla por su cuenta.
//
// De arriba abajo:
//
//   Título ⓘ
//   [Buscador] [Filtro] [Filtro] …                  [•••] [+ Nuevo …]
//   ─────────────────────────────────────────────────────────────────
//   Tabla, o uno de sus cuatro estados: cargando, error, vacío, sin resultados
//   Mostrando 25 filas                                            ‹ ›
//
// En el celular, la tabla se vuelve una lista de tarjetas (#183): deslizarla de
// lado dejaba fuera de la vista justo lo que se buscaba. Todo lo demás —la barra,
// los estados, el pie— queda igual.

/**
 * En qué lugar de la tarjeta del celular va una columna (#183):
 *
 *   ┌───────────────────────────────────────┐
 *   │ titulo                    esquina ••• │
 *   │ renglon                               │
 *   │ renglon                               │
 *   │ pie · pie · pie                       │
 *   └───────────────────────────────────────┘
 *
 * - `titulo`: una, en negrita. Sin ella el listado sigue con la tabla en el celular.
 * - `esquina`: una como máximo. Es el estado; sin estado, el dato que más decide.
 * - `renglon`: hasta dos, cada uno en su renglón.
 * - `pie`: las que hagan falta, juntas, en gris y separadas por « · ».
 *
 * Una columna sin lugar no sale en la tarjeta: se ve al abrir el registro. Si se
 * declaran más de las que caben, cuentan las primeras.
 */
export type LugarTarjeta = 'titulo' | 'esquina' | 'renglon' | 'pie';

export interface Columna<T> {
  id: string;
  titulo: string;
  /** Ancho en px, como en el Figma. Sin ancho, la columna toma lo que sobra. */
  ancho?: number;
  alinear?: 'inicio' | 'centro' | 'fin';
  celda: (fila: T) => ReactNode;
  /**
   * Dónde va en la tarjeta del celular. Basta el lugar —`tarjeta: 'pie'`— o, con
   * el objeto:
   *
   * - `conTitulo`: el título de la columna delante del dato, para el que se lee
   *   ambiguo suelto. Con dos fechas, «Entrega 20 oct», no «20 oct».
   * - `celda`: otra forma de dibujarlo, cuando la de la tabla no cabe en un renglón.
   * - `orden`: la posición en el pie, que se recorta por la derecha: lo que más
   *   importa va primero aunque en la tabla esté al final. Las que no lo tienen
   *   van después, en el orden de las columnas.
   */
  tarjeta?:
    | LugarTarjeta
    | { lugar: LugarTarjeta; conTitulo?: boolean; celda?: (fila: T) => ReactNode; orden?: number };
}

/**
 * Cómo se avanza por la lista. **Por cursor, no por número de página:** la API no
 * devuelve el total de filas y no lo va a devolver —el porqué está en
 * `CursorPage.cs` y en `Paginacion.tsx`—, así que el listado no sabe en qué
 * página está, solo si puede moverse.
 *
 * `hayMas` es el `hasMore` de `CursorPage`, tal cual viene. `hayAnterior` lo sabe
 * la pantalla, que es quien recuerda por qué cursores ya pasó.
 */
export interface PaginacionListado {
  hayAnterior: boolean;
  hayMas: boolean;
  onAnterior: () => void;
  onSiguiente: () => void;
}

interface Props<T> {
  titulo: string;
  /**
   * Qué es la pantalla, en el globo del ⓘ junto al título (#120). Obligatorio,
   * como en `Formulario`: toda pantalla explica para qué sirve.
   */
  info: string;
  columnas: readonly Columna<T>[];
  /** Las filas de la página actual. */
  filas: readonly T[] | undefined;
  claveFila: (fila: T) => string;

  /** De dónde vienen las filas. Con `error`, se muestra el estado de error y el botón de reintentar. */
  estado: 'cargando' | 'error' | 'listo';
  onReintentar: () => void;

  /** Lo de la izquierda de la barra: el `Buscador` y los `Filtro`. */
  filtros?: ReactNode;
  /** Lo de la derecha de la barra: «•••» y el botón de alta. */
  acciones?: ReactNode;

  /**
   * Si hay algún filtro puesto. Decide qué se muestra cuando no hay filas: sin
   * filtros, la tabla está vacía de verdad («Aún no hay…»); con filtros, es que
   * nada coincide, y se ofrece limpiarlos.
   */
  hayFiltros: boolean;
  onLimpiarFiltros: () => void;

  /** El estado vacío: «Aún no hay productos ni servicios» y qué hacer. */
  vacio: { titulo: string; texto: string; acciones?: ReactNode; nota?: string };
  /** La línea que explica qué no coincidió: «Ningún ítem coincide con «turbo»…». */
  detalleSinResultados?: string;
  /**
   * Qué hacer cuando nada coincide, en lugar de «Revisa la ortografía o prueba
   * con menos palabras», que solo es cierto si se escribió algo en el buscador.
   * Hoy lo usa Clientes, que distingue el buscador de los combos.
   */
  sugerenciaSinResultados?: string;

  /** Las opciones del «•••» de cada fila. Sin esto, la tabla no tiene esa columna. */
  opcionesFila?: (fila: T) => readonly ElementoMenu[];
  /**
   * Qué hacer al tocar una fila: abrir su ficha o su edición. Es para el dedo y
   * el mouse; un `<tr>` no recibe el foco, así que la pantalla deja además un
   * botón o enlace en alguna celda —el nombre— para quien usa el teclado.
   */
  onFila?: (fila: T) => void;

  paginacion: PaginacionListado;
}

export function Listado<T>({
  titulo,
  info,
  columnas,
  filas,
  claveFila,
  estado,
  onReintentar,
  filtros,
  acciones,
  hayFiltros,
  onLimpiarFiltros,
  vacio,
  detalleSinResultados,
  sugerenciaSinResultados,
  opcionesFila,
  onFila,
  paginacion,
}: Props<T>) {
  const { t } = useTraduccion();
  const tarjeta = partesDeTarjeta(columnas);
  const esCelular = useEsCelular();

  const sinFilas = estado === 'listo' && (filas?.length ?? 0) === 0;
  const catalogoVacio = sinFilas && !hayFiltros;
  const columnasTotales = columnas.length + (opcionesFila ? 1 : 0);

  const error = <EstadoError onReintentar={onReintentar} />;
  const sinResultados = (
    <EstadoSinResultados
      {...(detalleSinResultados && { detalle: detalleSinResultados })}
      {...(sugerenciaSinResultados && { sugerencia: sugerenciaSinResultados })}
      onLimpiarFiltros={onLimpiarFiltros}
    />
  );

  return (
    <section className="ui-listado">
      <div className="ui-listado__encabezado">
        <h1 className="ui-listado__titulo">{titulo}</h1>
        <Ayuda tema={titulo} texto={info} tono="titulo" />
      </div>

      <div className="ui-listado__barra">
        <div className="ui-listado__filtros">{filtros}</div>
        <div className="ui-listado__acciones">{acciones}</div>
      </div>

      <hr className="ui-listado__divisor" />

      {catalogoVacio ? (
        // El vacío de verdad reemplaza a la tabla entera, encabezado incluido:
        // no hay columnas que mirar todavía, hay algo que hacer.
        <div className="ui-listado__vacio">
          <h2>{vacio.titulo}</h2>
          <p>{vacio.texto}</p>
          {vacio.acciones && <div className="ui-listado__vacio-acciones">{vacio.acciones}</div>}
          {vacio.nota && <p className="ui-listado__vacio-nota">{vacio.nota}</p>}
        </div>
      ) : esCelular && tarjeta ? (
        // Los estados van sueltos, sin la celda que los centra en la tabla.
        <>
          {estado === 'cargando' && <p className="ui-tarjetas__aviso">{t('app.cargando')}</p>}
          {estado === 'error' && error}
          {sinFilas && sinResultados}
          {estado === 'listo' && !sinFilas && (
            <ul className="ui-tarjetas" aria-label={titulo}>
              {filas?.map((fila) => (
                <Tarjeta
                  key={claveFila(fila)}
                  fila={fila}
                  partes={tarjeta}
                  {...(opcionesFila && { opcionesFila })}
                  {...(onFila && { onFila })}
                />
              ))}
            </ul>
          )}
        </>
      ) : (
        <div className="ui-listado__marco">
          <table className="ui-tabla">
            <colgroup>
              {columnas.map((c) => (
                <col key={c.id} style={c.ancho ? { width: c.ancho } : undefined} />
              ))}
              {opcionesFila && <col style={{ width: 48 }} />}
            </colgroup>
            <thead>
              <tr>
                {columnas.map((c) => (
                  <th key={c.id} scope="col" className={alineacion(c.alinear)}>
                    {c.titulo}
                  </th>
                ))}
                {opcionesFila && (
                  <th scope="col">
                    <span className="ui-solo-lectores">{t('ui.listado.acciones')}</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {estado === 'cargando' && (
                <tr>
                  <td colSpan={columnasTotales} className="ui-tabla__aviso">
                    {t('app.cargando')}
                  </td>
                </tr>
              )}

              {estado === 'error' && (
                <tr>
                  <td colSpan={columnasTotales}>{error}</td>
                </tr>
              )}

              {sinFilas && (
                <tr>
                  <td colSpan={columnasTotales}>{sinResultados}</td>
                </tr>
              )}

              {estado === 'listo' &&
                filas?.map((fila) => (
                  <tr
                    key={claveFila(fila)}
                    {...(onFila && { className: 'ui-tabla__fila--tocable', onClick: alTocar(onFila, fila) })}
                  >
                    {columnas.map((c) => (
                      <td key={c.id} className={alineacion(c.alinear)}>
                        {c.celda(fila)}
                      </td>
                    ))}
                    {opcionesFila && (
                      <td className="ui-tabla__acciones">
                        <MenuFila elementos={opcionesFila(fila)} />
                      </td>
                    )}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {estado === 'listo' && !catalogoVacio && (
        <Pie cuantas={filas?.length ?? 0} paginacion={paginacion} />
      )}
    </section>
  );
}

/** Lo que va en un lugar de la tarjeta, ya resuelto: qué título y cómo se dibuja. */
interface Parte<T> {
  titulo: string;
  conTitulo: boolean;
  celda: (fila: T) => ReactNode;
}

interface PartesTarjeta<T> {
  titulo: Parte<T>;
  esquina: Parte<T> | undefined;
  renglones: Parte<T>[];
  pie: Parte<T>[];
}

/** Reparte las columnas en los lugares de la tarjeta. Sin `titulo`, no hay tarjeta. */
function partesDeTarjeta<T>(columnas: readonly Columna<T>[]): PartesTarjeta<T> | null {
  const partes = columnas.flatMap((c) => {
    if (!c.tarjeta) return [];
    const {
      lugar,
      conTitulo = false,
      celda = c.celda,
      orden = Infinity,
    } = typeof c.tarjeta === 'string' ? { lugar: c.tarjeta } : c.tarjeta;
    return [{ lugar, orden, parte: { titulo: c.titulo, conTitulo, celda } }];
  });
  // `sort` es estable: a igual `orden`, queda el de las columnas. Ordena una copia,
  // la que deja `filter`.
  const en = (lugar: LugarTarjeta) =>
    partes
      .filter((p) => p.lugar === lugar)
      .sort((a, b) => (a.orden === b.orden ? 0 : a.orden < b.orden ? -1 : 1))
      .map((p) => p.parte);

  const titulo = en('titulo')[0];
  if (!titulo) return null;
  return { titulo, esquina: en('esquina')[0], renglones: en('renglon').slice(0, 2), pie: en('pie') };
}

/**
 * Un dato vacío no ocupa lugar en la tarjeta: en la tabla, «—» guarda la columna;
 * en la tarjeta, un renglón con «—» solo es ruido.
 */
function esVacio(dato: ReactNode): boolean {
  return dato === null || dato === undefined || dato === false || dato === '' || dato === '—';
}

function Tarjeta<T>({
  fila,
  partes,
  opcionesFila,
  onFila,
}: {
  fila: T;
  partes: PartesTarjeta<T>;
  opcionesFila?: (fila: T) => readonly ElementoMenu[];
  onFila?: (fila: T) => void;
}) {
  // Sin la tabla no hay encabezados: el título de la columna va delante del dato,
  // a la vista si se pidió (`conTitulo`) o solo para el lector de pantalla.
  const dato = (parte: Parte<T>): ReactNode => {
    const contenido = parte.celda(fila);
    if (esVacio(contenido)) return null;
    return (
      <>
        {parte.conTitulo ? (
          <span className="ui-tarjeta__rotulo">{parte.titulo} </span>
        ) : (
          <span className="ui-solo-lectores">{parte.titulo}: </span>
        )}
        {contenido}
      </>
    );
  };

  const esquina = partes.esquina && dato(partes.esquina);
  const renglones = partes.renglones.map(dato).filter((d) => d !== null);
  const pie = partes.pie.map(dato).filter((d) => d !== null);

  return (
    <li
      className={onFila ? 'ui-tarjeta ui-tarjeta--tocable' : 'ui-tarjeta'}
      {...(onFila && { onClick: alTocar(onFila, fila) })}
    >
      <div className="ui-tarjeta__cabeza">
        <div className="ui-tarjeta__titulo">{partes.titulo.celda(fila)}</div>
        {esquina && <div className="ui-tarjeta__esquina">{esquina}</div>}
        {opcionesFila && <MenuFila elementos={opcionesFila(fila)} />}
      </div>
      {renglones.map((d, i) => (
        <div key={i} className="ui-tarjeta__renglon">
          {d}
        </div>
      ))}
      {pie.length > 0 && (
        <div className="ui-tarjeta__renglon ui-tarjeta__pie">
          {pie.map((d, i) => (
            <span key={i}>
              {i > 0 && <span aria-hidden="true"> · </span>}
              {d}
            </span>
          ))}
        </div>
      )}
    </li>
  );
}

/**
 * Tocar la fila —o la tarjeta— hace lo de `onFila`. Lo que tiene su propio clic
 * no la abre: el «•••», su menú y el botón del nombre, que ya hace lo mismo. Lo
 * de fuera del elemento —un menú en un portal, si llega a haberlo— sube por
 * React igual, y tampoco cuenta.
 */
function alTocar<T>(onFila: (fila: T) => void, fila: T) {
  return (e: MouseEvent<HTMLElement>) => {
    const destino = e.target as Element;
    if (!e.currentTarget.contains(destino)) return;
    if (destino.closest('button, a, input, select, textarea, [role="menu"]')) return;
    onFila(fila);
  };
}

/** El «•••» de una fila o de una tarjeta. */
function MenuFila({ elementos }: { elementos: readonly ElementoMenu[] }) {
  const { t } = useTraduccion();

  return (
    <Menu
      elementos={elementos}
      disparador={({ alternar, atributos }) => (
        <button
          type="button"
          className="ui-tabla__mas"
          aria-label={t('ui.listado.accionesFila')}
          onClick={alternar}
          {...atributos}
        >
          •••
        </button>
      )}
    />
  );
}

function EstadoError({ onReintentar }: { onReintentar: () => void }) {
  const { t } = useTraduccion();

  return (
    <div className="ui-listado__estado" role="alert">
      <h2>{t('ui.listado.error.titulo')}</h2>
      <p>{t('ui.listado.error.texto')}</p>
      <Boton variante="secundario" onClick={onReintentar}>
        {t('app.reintentar')}
      </Boton>
    </div>
  );
}

function EstadoSinResultados({
  detalle,
  sugerencia,
  onLimpiarFiltros,
}: {
  detalle?: string;
  sugerencia?: string;
  onLimpiarFiltros: () => void;
}) {
  const { t } = useTraduccion();

  return (
    <div className="ui-listado__estado">
      <h2>{t('ui.listado.sinResultados.titulo')}</h2>
      {detalle && <p>{detalle}</p>}
      <p>{sugerencia ?? t('ui.listado.sinResultados.texto')}</p>
      <Boton variante="secundario" onClick={onLimpiarFiltros}>
        {t('ui.listado.limpiarFiltros')}
      </Boton>
    </div>
  );
}

/**
 * «Mostrando 25 filas» y las flechas.
 *
 * Dice cuántas se ven, no cuántas hay: sin total no se puede prometer «de 48», y
 * un número inventado es peor que no ponerlo.
 */
function Pie({ cuantas, paginacion }: { cuantas: number; paginacion: PaginacionListado }) {
  const { t } = useTraduccion();

  return (
    <div className="ui-listado__pie">
      <span>{t('ui.listado.mostrando', { n: cuantas })}</span>
      <Paginacion {...paginacion} />
    </div>
  );
}

function alineacion(alinear: Columna<unknown>['alinear']): string | undefined {
  if (alinear === 'centro') return 'ui-tabla__centro';
  if (alinear === 'fin') return 'ui-tabla__fin';
  return undefined;
}
